import {
  Scissors, Sparkles, Eye, PartyPopper,
  Presentation, ChefHat, Heart, Plane, Cannabis, ShoppingBag
} from "lucide-react";

// Image-based category icons - all 11 categories use custom PNGs
const CategoryImageIcon = ({ src, alt, className, style }) => {
  const sizeMatch = (className || '').match(/w-(\d+)/);
  const size = sizeMatch ? parseInt(sizeMatch[1]) * 4 : 20;
  return (
    <img 
      src={src} 
      alt={alt} 
      className={className}
      style={{ width: size, height: size, objectFit: 'contain', display: 'inline-block', ...style }}
    />
  );
};

const HairImageIcon = (props) => <CategoryImageIcon src="/category-icons/hair.png" alt="Hair" {...props} />;
const MakeupImageIcon = (props) => <CategoryImageIcon src="/category-icons/makeup.png" alt="Makeup" {...props} />;
const NailImageIcon = (props) => <CategoryImageIcon src="/category-icons/nail.png" alt="Nail" {...props} />;
const LashImageIcon = (props) => <CategoryImageIcon src="/category-icons/lash.png" alt="Lash" {...props} />;
const EventsImageIcon = (props) => <CategoryImageIcon src="/category-icons/events.png" alt="Events" {...props} />;
const InstructorImageIcon = (props) => <CategoryImageIcon src="/category-icons/instructor.png" alt="Instructor" {...props} />;
const CulinaryImageIcon = (props) => <CategoryImageIcon src="/category-icons/culinary.png" alt="Culinary" {...props} />;
const WellnessImageIcon = (props) => <CategoryImageIcon src="/category-icons/wellness.png" alt="Wellness" {...props} />;
const TravelImageIcon = (props) => <CategoryImageIcon src="/category-icons/travel.png" alt="Travel" {...props} />;
const DispensaryImageIcon = (props) => <CategoryImageIcon src="/category-icons/dispensary.png" alt="Dispensary" {...props} />;
const RetailImageIcon = (props) => <CategoryImageIcon src="/category-icons/retail.png" alt="Retail" {...props} />;

/**
 * SINGLE SOURCE OF TRUTH for all category colors, icons, and labels.
 * Every component that renders category-specific UI MUST import from here.
 * 
 * iconColor: the icon stroke/fill color (hex)
 * bgColor: the circle/badge background behind the icon (hex)
 * name: display name for admin/dashboard contexts
 * inspoLabel: display name for /me Inspiration tab (e.g. "Food" instead of "Culinary")
 * icon: Lucide React icon component
 */

export const CATEGORY_PRESETS = {
  hair: {
    key: "hair",
    name: "Hair",
    inspoLabel: "Hair",
    icon: HairImageIcon,
    iconColor: "#e83e8c",
    bgColor: "#ffeef4",
  },
  makeup: {
    key: "makeup",
    name: "Makeup",
    inspoLabel: "Face",
    icon: MakeupImageIcon,
    iconColor: "#9c57f5",
    bgColor: "#f2e9ff",
  },
  nails: {
    key: "nails",
    name: "Nails",
    inspoLabel: "Nails",
    icon: NailImageIcon,
    iconColor: "#f15a70",
    bgColor: "#ffe8ec",
  },
  lashes: {
    key: "lashes",
    name: "Lashes",
    inspoLabel: "Lashes",
    icon: LashImageIcon,
    iconColor: "#774cda",
    bgColor: "#ede7f7",
  },
  events: {
    key: "events",
    name: "Events",
    inspoLabel: "Style",
    icon: EventsImageIcon,
    iconColor: "#f9c434",
    bgColor: "#fff5e6",
  },
  instructors: {
    key: "instructors",
    name: "Instructors",
    inspoLabel: "Instructors",
    icon: InstructorImageIcon,
    iconColor: "#4a90d9",
    bgColor: "#e0eeff",
  },
  culinary: {
    key: "culinary",
    name: "Culinary",
    inspoLabel: "Food",
    icon: CulinaryImageIcon,
    iconColor: "#f28c28",
    bgColor: "#fff3e6",
  },
  wellness: {
    key: "wellness",
    name: "Wellness",
    inspoLabel: "Wellness",
    icon: WellnessImageIcon,
    iconColor: "#75d6d6",
    bgColor: "#e7f5f5",
  },
  travel: {
    key: "travel",
    name: "Travel",
    inspoLabel: "Travel",
    icon: TravelImageIcon,
    iconColor: "#73b7ff",
    bgColor: "#e7f3ff",
  },
  dispensary: {
    key: "dispensary",
    name: "Dispensary",
    inspoLabel: "Dispensary",
    icon: DispensaryImageIcon,
    iconColor: "#60d680",
    bgColor: "#e8fef0",
  },
  retail: {
    key: "style",
    name: "Style",
    inspoLabel: "Style",
    icon: RetailImageIcon,
    iconColor: "#9950fa",
    bgColor: "#f0e8ff",
  },
  // Slice A - explicit "style" preset alias so getAllCategories() resolves it
  style: {
    key: "style",
    name: "Style",
    inspoLabel: "Style",
    icon: RetailImageIcon,
    iconColor: "#9950fa",
    bgColor: "#f0e8ff",
  },
};

// Ordered list of all category keys for consistent rendering
export const CATEGORY_ORDER = [
  "hair", "makeup", "nails", "lashes", "events",
  "instructors", "culinary", "wellness", "travel",
  "dispensary", "style"
];

// Alias map for legacy keys (nail -> nails, lash -> lashes, event -> events, instructor -> instructors)
const ALIASES = {
  nail: "nails",
  lash: "lashes",
  event: "events",
  instructor: "instructors",
  face: "makeup",       // /me Inspo tab labels
  style: "style",      // /me Inspo tab labels (was renamed to "Style")
  food: "culinary",     // /me Inspo tab labels (was renamed to "Food")
};

/**
 * Get a category preset by key, resolving aliases.
 * Iter 9.38 - merges in any runtime overrides set via setLiveCategoryOverride()
 * (called by useLiveCategoryMeta) so every consumer automatically gets fresh
 * admin colors / icons without having to refactor 38 import sites.
 * @param {string} key - Category key (e.g. "hair", "nail", "lash")
 * @returns {object|null} Category preset or null
 */

// Iter 9.38 - runtime cache populated by useLiveCategoryMeta
const LIVE_OVERRIDES = {};
export function setLiveCategoryOverride(key, partial) {
  if (!key || !partial) return;
  const k = key.toLowerCase().trim();
  LIVE_OVERRIDES[k] = { ...(LIVE_OVERRIDES[k] || {}), ...partial };
}
export function getLiveCategoryOverride(key) {
  if (!key) return null;
  return LIVE_OVERRIDES[key.toLowerCase().trim()] || null;
}

export function getCategoryPreset(key) {
  if (!key) return null;
  const normalized = key.toLowerCase().trim();
  const base = CATEGORY_PRESETS[normalized] || CATEGORY_PRESETS[ALIASES[normalized]] || null;
  if (!base) return null;
  const override = LIVE_OVERRIDES[normalized];
  if (!override) return base;
  // Merge live admin overrides on top of static defaults
  return {
    ...base,
    ...(override.label ? { name: override.label } : {}),
    ...(override.light_hex ? { bgColor: override.light_hex } : {}),
    ...(override.dark_hex ? { iconColor: override.dark_hex } : {}),
    ...(override.icon_url ? { icon_url: override.icon_url } : {}),
  };
}

/**
 * Get all category presets as an ordered array.
 * Iter 9.40 Slice C⁷ - merges live overrides so any consumer reading
 * `getAllCategories()` (sidebars, banners, host dashboards) automatically
 * sees the latest Master Components colors + icons without a refactor.
 * @returns {object[]} Array of category presets in display order
 */
export function getAllCategories() {
  return CATEGORY_ORDER.map(k => getCategoryPreset(k)).filter(Boolean);
}

/**
 * Get Tailwind-compatible class strings for a category.
 * Useful for places that still need Tailwind classes.
 */
export function getCategoryTailwind(key) {
  const preset = getCategoryPreset(key);
  if (!preset) return { bg: "bg-gray-100", text: "text-gray-600", dot: "bg-gray-400", border: "border-gray-200" };
  return {
    bg: `bg-[${preset.bgColor}]`,
    text: `text-[${preset.iconColor}]`,
    dot: `bg-[${preset.iconColor}]`,
    border: `border-[${preset.iconColor}]/30`,
  };
}

// Iter 9.40 Slice C⁸ - re-export refreshLiveCategoryMeta from the hook module
// so consumers can import the full live-category helper triplet from a single
// path (categoryConfig). Resolves the compile-error RCA from iter_385.
export { refreshLiveCategoryMeta } from "../hooks/useLiveCategoryMeta";
